import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { filterActiveVehicles, getPreferredVehicle, getUniqueInternalSubscriptions } from '../../shared/utils/general';
import { SiriusXMSubscription, UserPreferredVehicle } from '../../shared/typings/main';
import { useFeatureServices } from '../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../shared/hooks/use-my-vw-context';
import { useUser } from './UserContext';

interface VehicleContextType {
  isLoadingPreferredVehicle: boolean;
  isLoadingVehicleData: boolean;
  isUpdatingNickname: boolean;
  userPreferredVehicle?: UserPreferredVehicle;

  fetchAdditionalVehiclesInformation: () => Promise<void>;
  refreshPreferredVehicle: () => Promise<void>;
  removePreferredVehicleUIonly: () => void;
  resetPreferredVehicle: () => void;
  updateVehicleNickname: (nickname: string) => Promise<void>;
}

const VehicleContext = createContext<VehicleContextType | undefined>(undefined);

export const VehicleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoadingPreferredVehicle, setIsLoadingPreferredVehicle] = useState(true);
  const [isLoadingVehicleData, setIsLoadingVehicleData] = useState<boolean>(false);
  const [isUpdatingNickname, setIsUpdatingNickname] = useState<boolean>(false);
  const [userPreferredVehicle, setUserPreferredVehicle] = useState<UserPreferredVehicle | undefined>(undefined);

  const { authService } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();
  const { userAccountDetails, userAccountPrivileges, updateUserProfile, refetchUserProfile } =
    useUser();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  const fetchVehicleAdditionalUsers = async (vehicleId: string) => {
    const { getUserAccountDetailsByVehicle } =
      await authService.getUserAccountDetailsByVehicleWithoutActiveVehicleFilterGqlRequest(
        vehicleId,
        localeCode
      );

    if (getUserAccountDetailsByVehicle?.allAssociatedUsers) {
      return getUserAccountDetailsByVehicle?.allAssociatedUsers;
    }

    return [];
  };

  const fetchVehicleSubscriptions = async (vehicleId: string) => {
    const { getVehicleSubscriptions } =
      await authService.getVehicleSubscriptionsGqlRequest(vehicleId, localeCode);

    if (getVehicleSubscriptions?.data) {
      return {
        subscriptions: getVehicleSubscriptions.data,
      };
    }

    return null;
  };

  const fetchVehicleExternalSubscriptions = async (
    vehicleId: string
  ): Promise<SiriusXMSubscription | null> => {
    const { getVehicleExternalSubscriptions } =
      await authService.getVehicleExternalSubscriptionsGqlRequest(vehicleId, localeCode);

    if (getVehicleExternalSubscriptions.data) {
      return getVehicleExternalSubscriptions.data;
    }

    return null;
  };

  const fetchAdditionalVehiclesInformation = useCallback(async () => {
    if (userAccountDetails && userAccountPrivileges.length > 0) {
      setIsLoadingVehicleData(true);
      const activeVehicles = filterActiveVehicles(
        userAccountDetails?.vehicles || [],
        userAccountPrivileges || [],
        localeService?.countryCode,
        localeService?.language,
      );

      try {
        const enhancedVehicles = [...activeVehicles];

        await Promise.all(
          enhancedVehicles.map(async (vehicle) => {
            const vehicleId: string = vehicle.vehicleId || '';

            const [subscriptionData, siriusXMData, associatedUsers] =
              await Promise.all([
                fetchVehicleSubscriptions(vehicleId),
                fetchVehicleExternalSubscriptions(vehicleId),
                fetchVehicleAdditionalUsers(vehicleId),
              ]);

            if (subscriptionData) {
              vehicle.subscriptions = subscriptionData.subscriptions;
              vehicle.internalSubscriptions = getUniqueInternalSubscriptions([
                vehicle,
              ]);
            } else {
              vehicle.subscriptions = { subscribedPlans: [], allPlans: [] };
              vehicle.internalSubscriptions = [];
            }

            vehicle.siriusXMSubscription = siriusXMData || null;
            vehicle.associatedUsers = associatedUsers || [];
          })
        );

        updateUserProfile({ vehicles: enhancedVehicles });
      } catch (error) {
        console.error('Error fetching vehicle information:', error);
      } finally {
        setIsLoadingVehicleData(false);
      }
    }
  }, [userAccountDetails, userAccountPrivileges, localeService, authService, updateUserProfile]);

  const fetchPreferredVehicle = useCallback(async () => {
    setIsLoadingPreferredVehicle(true);

    if (!userAccountDetails) return;

    try {
      if (!userAccountDetails?.vehicles?.length) {
        setUserPreferredVehicle(undefined);
        return;
      }

      const activeVehicles = filterActiveVehicles(
        userAccountDetails?.vehicles || [],
        userAccountPrivileges || [],
        localeService?.countryCode,
        localeService?.language,
      );

      const preferredVehicle =
        activeVehicles.length > 1
          ? getPreferredVehicle(activeVehicles)
          : activeVehicles[0];

      if (!preferredVehicle?.vehicleId) {
        setUserPreferredVehicle(undefined);
        return;
      }

      const [getVehicleByVehicleId, { getNickNameOfACar }] = await Promise.all([
        authService.getVehicleByVehicleIdGqlRequest(preferredVehicle.vehicleId, localeCode),
        authService.getUserVehicleNickname(preferredVehicle, localeCode),
      ]);

      setUserPreferredVehicle({
        ...getVehicleByVehicleId,
        vehicleNickname: getNickNameOfACar.carNameDesc,
      });

    } catch (error) {
      console.error('Error fetching preferred vehicle', error);
      setUserPreferredVehicle(undefined);
      setIsLoadingPreferredVehicle(false);
    } finally {
      setIsLoadingPreferredVehicle(false)
    }
  }, [userAccountDetails]);

  const refreshPreferredVehicle = useCallback(async () => {
    setIsLoadingPreferredVehicle(true);

    await fetchPreferredVehicle();
  }, [fetchPreferredVehicle]);

  const resetPreferredVehicle = useCallback(() => {
    setIsLoadingPreferredVehicle(true);
    setUserPreferredVehicle(undefined);
  }, []);

  useEffect(() => {
    fetchPreferredVehicle();
  }, [fetchPreferredVehicle]);

  const updateVehicleNickname = async (nickname: string) => {
    setIsUpdatingNickname(true);

    try {
      await authService.saveUserVehicleNickName(nickname, localeCode);

      if (userPreferredVehicle) {
        setUserPreferredVehicle({
          ...userPreferredVehicle,
          vehicleNickname: nickname,
        });
      }

      await refreshPreferredVehicle();
    } catch (error) {
      console.error('Error updating vehicle nickname:', error);
      throw error;
    } finally {
      setIsUpdatingNickname(false);
    }
  };

  const removePreferredVehicleUIonly = async () => {
    await refetchUserProfile()
    setUserPreferredVehicle(undefined);
  };

  useEffect(() => {
    fetchAdditionalVehiclesInformation();
  }, [fetchAdditionalVehiclesInformation]);

  return (
    <VehicleContext.Provider
      value={{
        fetchAdditionalVehiclesInformation,
        isLoadingPreferredVehicle,
        isLoadingVehicleData,
        isUpdatingNickname,
        refreshPreferredVehicle,
        removePreferredVehicleUIonly,
        resetPreferredVehicle,
        updateVehicleNickname,
        userPreferredVehicle,
      }}>
      {children}
    </VehicleContext.Provider>
  );
};

export const useVehicle = () => {
  const context = useContext(VehicleContext);

  if (!context)
    throw new Error('useVehicle must be used within VehicleProvider');

  return context;
};